import {
  AddDiscountToOrderResponse,
  OrdersDtoOrderLineDto,
  ShoppingCartResponse,
  SimpleShoppingCartResponse,
} from "@springtree/eva-services-core";
import { useSearchParams } from "next/navigation";
import { useRouter } from "next/router";
import Script from "next/script";
import { useTranslations } from "next-intl";
import { createContext, PropsWithChildren, useContext, useEffect, useRef, useState } from "react";

import IconClose from "@/icons/icon-close";
import Icon from "~/components/common/icon";
import { PagePopup, PagePopupClose, PagePopupContent } from "~/components/common/page-popup";
import { useCartWithHooks, useCartWithPendingStatus } from "~/hooks/use-cart";
import Constants from "~/utils/constants";
import { getCouponErrorMessage } from "~/utils/coupon-utils";

type ContextAttrs = {
  currentCoupon?: OrdersDtoOrderLineDto;
  applyCoupon: (code: string) => Promise<AddDiscountToOrderResponse | undefined>;
  removeCoupon: () => Promise<SimpleShoppingCartResponse | undefined>;
};

type ContextProps = {} & PropsWithChildren;

const Context = createContext<ContextAttrs | undefined>(undefined);

function _createAddCouponRequest(couponCode: string) {
  return {
    orderType: 0,
    payload: {
      CouponCode: couponCode,
    },
  };
}

function _findCouponEntryLine(cart: ShoppingCartResponse | undefined) {
  return cart?.ShoppingCart?.Lines?.find((line) => line.DiscountCouponCode);
}

export default function GlobalCouponProvider(props: ContextProps) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { isLoadingCart } = useCartWithPendingStatus();
  const { cart, addCouponCode, cancelDiscountOrderLine, couponError, setCouponError } = useCartWithHooks();
  const refCart = useRef<ShoppingCartResponse | undefined>(undefined);
  const refLastCode = useRef<string | undefined>(undefined);
  const [feedback, setFeedback] = useState("");
  const [currentCoupon, setCurrentCoupon] = useState<OrdersDtoOrderLineDto | undefined>(undefined);

  const t = useTranslations();
  const refreshCart = () => {
    setTimeout(() => {}, 1000);
  };

  const applyCoupon = async (code: string): Promise<AddDiscountToOrderResponse | undefined> => {
    refLastCode.current = code;
    return addCouponCode(_createAddCouponRequest(code)).then((response) => {
      setCurrentCoupon(_findCouponEntryLine(cart));
      return response;
    });
  };

  const removeCoupon = async (): Promise<SimpleShoppingCartResponse | undefined> => {
    if (!currentCoupon) {
      throw new Error("No coupon to remove");
    }
    return cancelDiscountOrderLine({
      payload: {
        OrderLineID: currentCoupon.ID,
      },
    })
      .then((response) => {
        if (!response?.Error) {
          setCurrentCoupon(undefined);
        }
        return response;
      })
      .catch((_err) => {
        return undefined;
      });
  };

  useEffect(() => {
    if (isLoadingCart || !router?.isReady) {
      return;
    }

    const paramCode = searchParams.get(Constants.COUPON_KEY);
    if (paramCode) {
      if (!cart) {
        setFeedback(t("generic.needsCart"));
        return;
      }

      if (refCart && refCart.current === undefined) {
        applyCoupon(paramCode)
          .then((response) => {
            const applied = response && response.DiscountID && response.DiscountIsApplied;
            setFeedback(t(applied ? "generic.codeApplied" : "error.invalid_coupon_combination"));
          })
          .catch((_err) => {
            // ignore and log after
          })
          .finally(() => {
            setCurrentCoupon(_findCouponEntryLine(cart));
          });
        refCart.current = cart;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingCart, cart, router?.isReady]);

  // handle async error from AddDiscountCouponToOrder, reporting what EVA answered
  useEffect(() => {
    if (couponError) {
      const code = refLastCode.current ?? searchParams.get(Constants.COUPON_KEY) ?? undefined;
      setFeedback(getCouponErrorMessage(couponError, t, code));
      setCouponError(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponError, setCouponError]);

  return (
    <>
      {feedback && (
        <PagePopup initialOpen={true} closesOn="click">
          <PagePopupContent>
            <p style={{ paddingInlineEnd: "28px" }}>{feedback}</p>
            <PagePopupClose>
              <Icon icon={IconClose} width={16} height={16} />
            </PagePopupClose>
          </PagePopupContent>
        </PagePopup>
      )}
      <Context.Provider value={{ applyCoupon, currentCoupon, removeCoupon }}>{props.children}</Context.Provider>
      <Script src="https://static.fanplayr.com/client/sw-load.js" data-path="/next-service-worker.js" />
    </>
  );
}

export function useGlobalCoupon() {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error("useGlobalCoupon must be used within a GlobalCouponProvider");
  }
  return ctx;
}
